import { EntityType } from '@frontend/common';
import { UserGroupClient } from '@frontend/group/api';
import type { GroupMember, IGroup } from '@frontend/group/types';

const sortMembersByOrder = (members: GroupMember[]): GroupMember[] => [...members].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/**
 * Fetches user-group members sorted by `order`, keeps only nested user_group members
 * (direct users are omitted), resolves them to groups, and returns groups in member order.
 */
export const resolveNestedUserGroupsByMemberOrder = async (groupId: string): Promise<IGroup[]> => {
    const membersResponse = await UserGroupClient.fetchUserGroupMembers({
        group_id: groupId,
        sort_field: 'order',
        sort_direction: 'asc'
    });
    const orderedGroupIds = sortMembersByOrder(membersResponse.results)
        .filter((member) => member.entity_type === EntityType.USER_GROUP)
        .map((member) => member.entity_id);

    if (orderedGroupIds.length === 0) {
        return [];
    }

    const groupsResponse = await UserGroupClient.resolveUserGroups(orderedGroupIds);
    const groupsById = new Map(groupsResponse.results.map((group) => [group.id, group]));

    return orderedGroupIds.map((id) => groupsById.get(id)).filter((group): group is IGroup => !!group);
};
